import { formatAcquiredAt } from '@/components/Functional/utils/datetime'
import { Button } from '@/components/UI/Button'
import { Dialog } from '@/components/UI/Dialog'
import { IconChevronRight } from '@tabler/icons-react'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

export type StampDetailInfo = {
  id: number
  name: string
  image: string | null
  point: number
  /** 取得済みか */
  isHas: boolean
  /** 獲得日時（ISO8601）。ポイントカード経由で開いたときのみ渡される（#2970） */
  acquiredAt?: string | null
  spots: {
    id: number
    name: string
    /** スポットサムネイル画像URL */
    image?: string | null
    address?: string
    zip?: string
  }[]
}

type Props = {
  stamp: StampDetailInfo | null
  isOpen: boolean
  onClose: () => void
  /** スポット行タップ時に呼ばれる。spotId を渡す */
  onCheckin: (spotId: number) => void
  /** スタンプの形状（0: 丸, 1: 四角）。省略時は 0（丸）として扱う */
  stampShape?: 0 | 1
}

/** スタンプ詳細ダイアログ */
export const DialogStampDetail = memo(({ stamp, isOpen, onClose, onCheckin, stampShape }: Props) => {
  const { t } = useTranslation()
  if (!stamp) return null

  const isSquare = stampShape === 1
  const stampImageClass = isSquare ? 'w-32 h-32 object-cover' : 'w-32 h-32 object-cover rounded-full'
  const stampPlaceholderClass = isSquare
    ? 'w-32 h-32 bg-gray-100 flex items-center justify-center text-gray-400 text-xs'
    : 'w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 text-xs'

  return (
    <Dialog selectorState={isOpen} closeFunc={onClose} title={t('home.stamp_detail.title', 'スタンプ詳細')}>
      <div className="flex flex-col items-center gap-4">
        {/* スタンプ画像 */}
        {stamp.image ? (
          <img src={stamp.image} alt={stamp.name} className={stampImageClass} />
        ) : (
          <div className={stampPlaceholderClass}>No Image</div>
        )}

        {/* スタンプ名＋獲得日時（獲得日時はポイントカード経由のときのみ・#2970）。間隔は 4px（gap-1） */}
        <div className="mb-4 flex flex-col items-center gap-1">
          <h2 className="text-[16px] font-bold text-black text-center">{stamp.name}</h2>
          {stamp.acquiredAt && (
            <p className="text-[12px] text-gray-600 text-center">
              {/* コロンの形式は言語で異なる（fr は「 : 」等）ため文全体を補間キーにする */}
              {t('home.stamp_detail.acquired_at', '獲得日時：{{datetime}}', {
                datetime: formatAcquiredAt(stamp.acquiredAt),
              })}
            </p>
          )}
        </div>

        {/* スポット一覧 */}
        <div className="w-full">
          {/* 獲得可能スポット */}
          <p className="text-[16px] font-bold text-black text-center">
            {t('home.stamp_detail.available_spots', '獲得可能スポット')}
          </p>
          {stamp.spots.map((spot) => (
            <button
              key={spot.id}
              type="button"
              onClick={() => {
                onCheckin(spot.id)
                onClose()
              }}
              className="w-full flex items-center gap-3 px-0 mt-2 pb-2 h-20 border-b border-[#616161] last:border-b-0 bg-transparent text-left"
            >
              {/* スポットサムネイル */}
              {spot.image ? (
                <img src={spot.image} alt={spot.name} className="w-16 h-16 rounded-2xl object-cover shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-gray-200 shrink-0 flex items-center justify-center text-gray-400 text-xs">
                  No Img
                </div>
              )}
              {/* スポット情報 */}
              <div className="flex-1 min-w-0">
                <p className="text-[16px] font-bold text-black leading-6 truncate">{spot.name}</p>
                <p className="text-[11px] font-bold text-black leading-4">{spot.address}</p>
              </div>
              <IconChevronRight size={24} className="shrink-0 text-[#cbcbce]" aria-hidden="true" />
            </button>
          ))}
        </div>

        {/* OK ボタン */}
        <div className="flex justify-center w-full">
          <Button label={t('common.close', '閉じる')} variant="primary" size="md" onClick={onClose} fullWidth />
        </div>
      </div>
    </Dialog>
  )
})
